import { ActivityIndicator, Pressable, View, type ViewProps } from "@-/com.npmjs.react-native";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import type { ReactNode } from "react";

/** Bordered chrome of a Deck card surface: a title/count header strip above the cards. */
export function DeckCardSurfaceFrame({
  accessibilityLabel,
  cardCount,
  children,
  countLabel,
  isRefetching,
  onHeaderLayout,
  onLayout,
  onToggleDetails,
  showDetails,
  subtitle,
  testID,
  title,
}: {
  accessibilityLabel?: string;
  /** Omitted while the count is still unknown (loading fallbacks). */
  cardCount?: number;
  children: ReactNode;
  /** Review surfaces put their count on the centered subtitle line. */
  countLabel?: string;
  isRefetching?: boolean;
  onHeaderLayout?: ViewProps["onLayout"];
  onLayout?: ViewProps["onLayout"];
  onToggleDetails?: () => void;
  showDetails?: boolean;
  subtitle?: string;
  testID: string;
  title: string;
}) {
  return (
    <View
      accessibilityLabel={accessibilityLabel}
      className="mx-1 mb-1 overflow-hidden rounded-xs border border-border bg-surface"
      onLayout={onLayout}
      testID={testID}
    >
      <View
        // The Upcoming/Past review surface sets its header apart with the off-white strip.
        className={
          countLabel === undefined
            ? "flex-row items-center justify-between gap-3 border-b border-border px-4 py-3"
            : "flex-row items-center justify-between gap-3 border-b border-border bg-surface-header px-4 py-3"
        }
        onLayout={onHeaderLayout}
        testID={`${testID}-header`}
      >
        {countLabel === undefined ? null : (
          <View className="size-8 items-center justify-center">
            {isRefetching ? (
              <ActivityIndicator accessibilityLabel="Refreshing cards" size="small" />
            ) : null}
          </View>
        )}
        <View className="min-w-0 flex-1 gap-0.5">
          <Text
            accessibilityRole="header"
            className={
              countLabel === undefined
                ? "text-base font-semibold"
                : "text-center text-base font-semibold"
            }
          >
            {title}
          </Text>
          {countLabel === undefined ? null : (
            <Text className="text-center text-xs text-muted-foreground tabular-nums">
              {countLabel}
              {cardCount === undefined
                ? ""
                : `: ${cardCount} ${cardCount === 1 ? "card" : "cards"}`}
            </Text>
          )}
          {subtitle === undefined ? null : (
            <Text
              className={
                countLabel === undefined
                  ? "text-xs text-muted-foreground"
                  : "text-center text-xs text-muted-foreground"
              }
            >
              {subtitle}
            </Text>
          )}
        </View>
        {isRefetching && countLabel === undefined ? (
          <ActivityIndicator accessibilityLabel="Refreshing cards" size="small" />
        ) : null}
        {cardCount === undefined || countLabel !== undefined ? null : (
          <Text className="text-sm text-muted-foreground tabular-nums">
            {cardCount} {cardCount === 1 ? "card" : "cards"}
          </Text>
        )}
        {countLabel === undefined ? null : onToggleDetails === undefined ? (
          <View className="size-8" />
        ) : (
          <Pressable
            accessibilityLabel={showDetails ? "Show minimal glyph tiles" : "Show detailed tiles"}
            accessibilityRole="button"
            accessibilityState={{ expanded: showDetails }}
            className="size-8 items-center justify-center rounded-xs active:bg-accent"
            hitSlop={6}
            onPress={onToggleDetails}
            testID={`${testID}-details-toggle`}
          >
            <Text className="text-xl">{showDetails ? "−" : "+"}</Text>
          </Pressable>
        )}
      </View>
      {children}
    </View>
  );
}
